import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotifications } from '@/contexts/NotificationContext';
import { useToast } from '@/hooks/use-toast';
import { isWithinInterval, differenceInMinutes, addMinutes } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { BREAK_GRACE_CAP_FRACTION } from '@/constants/schedulingConstants';
// DTC-B: route the break-monitor "now" anchor through the canonical
// clock so timer-in-break detection is deterministic under
// `clock.setFixed(...)` test fixtures. The `new Date(ticket.actual_start_time)`
// site at L94 parses an existing stored timestamp and is intentionally
// NOT routed through the clock.
import { clock } from '@/utils/clock';

interface Break {
  start: string;
  end: string;
  name: string;
  date?: string;
}

/**
 * Hook to monitor timers running during breaks with incremental grace period
 * NEW PHASE 2: Implements minute-by-minute break sliding up to ½ break length
 */
// BUG-5: Accept string label and shopTimezone instead of Date — no more browser TZ split
// R5-H-2: Added shopId param to scope queries to current shop only
export const useTimerBreakMonitor = (selectedDayLabel: string, shopTimezone: string, shopId: string | null) => {
  const { addNotification } = useNotifications();
  const { toast } = useToast();

  useEffect(() => {
    const checkTimersInBreaks = async () => {
      try {
        // BUG-7: selectedDayLabel is already the canonical YYYY-MM-DD string
        const dateStr = selectedDayLabel;
        // R5-H-2: Guard — no shopId means we can't scope queries correctly
        if (!shopId) return;
        const now = clock.now();
        
        // Get all active tickets with running timers - bypass type check.
        // R125-H-1: narrow select — break-monitor only reads id/status/
        // actual_start_time/assigned_tech_id/customer_name. select('*')
        // previously pulled customer_phone/customer_email into the React
        // Query cache for every Active ticket every 30s. customer_name is
        // retained for the toast text on line 159.
        const ticketsResult = await (supabase as any)
          .from('tickets')
          .select('id, status, actual_start_time, assigned_tech_id, customer_name')
          .eq('scheduled_date', dateStr)
          .eq('shop_id', shopId)
          .eq('parking_lot', false) // LOW 4.6: Exclude parking lot tickets
          .eq('actually_started', true);

        if (ticketsResult.error || !ticketsResult.data) return;
        
        // Filter for ONLY active tickets with actual_start_time (not already paused)
        const activeTickets = ticketsResult.data.filter(
          (t: any) => t.status === 'Active' && t.actual_start_time
        );
        
        if (activeTickets.length === 0) return;

        // Get technicians with their breaks - bypass type check
        const techIds = activeTickets.map((t: any) => t.assigned_tech_id);
        // BUG-4 micro-guard: .in('id', []) behaves inconsistently — bail early on empty boards
        if (!techIds.length) return;
        // BUG-4: Scope fetch to only the techs with active tickets — techIds is already shop-scoped.
        // R125-H-2: narrow select — break-monitor only reads id/name/
        // recurring_breaks/non_recurring_breaks. Dropping the rest keeps
        // efficiency_percentage, shift_start/end, proficiencies, and any
        // future tech fields out of React Query cache.
        const techsResult = await (supabase as any)
          .from('technicians')
          .select('id, name, recurring_breaks, non_recurring_breaks')
          .in('id', techIds);

        if (techsResult.error || !techsResult.data) return;

        const technicians = techsResult.data.filter((t: any) => 
          techIds.includes(t.id)
        );
        
        if (technicians.length === 0) return;

        for (const ticket of activeTickets) {
          const tech = technicians.find((t: any) => t.id === ticket.assigned_tech_id);
          if (!tech) continue;

          // Combine recurring and non-recurring breaks for today
          const recurringBreaks = (tech.recurring_breaks || []) as unknown as Break[];
          const nonRecurringBreaks = ((tech.non_recurring_breaks || []) as unknown as Break[])
            .filter((br: Break) => br.date === dateStr);
          
          const allBreaks = [...recurringBreaks, ...nonRecurringBreaks];
          
          // Check if timer was STARTED during a break - treat like "started early", ignore auto-pause
          const timerStartTime = ticket.actual_start_time ? new Date(ticket.actual_start_time) : null;
          if (timerStartTime) {
            const wasStartedDuringBreak = allBreaks.some((breakInfo: Break) => {
              // BUG-5: Use shop TZ so break windows are viewer-TZ-independent
              const breakStart = fromZonedTime(`${dateStr}T${breakInfo.start}`, shopTimezone);
              const breakEnd = fromZonedTime(`${dateStr}T${breakInfo.end}`, shopTimezone);
              return isWithinInterval(timerStartTime, { start: breakStart, end: breakEnd });
            });
            
            if (wasStartedDuringBreak) {
              // Timer was deliberately started during a break - skip auto-pause entirely
              console.log(`[Break Monitor] Ticket ${ticket.id}: Timer started during break - ignoring auto-pause`);
              continue; // Skip this ticket entirely
            }
          }

          for (const breakInfo of allBreaks) {
            // BUG-5: Shop-TZ break window parsing
            const breakStart = fromZonedTime(`${dateStr}T${breakInfo.start}`, shopTimezone);
            const breakEnd = fromZonedTime(`${dateStr}T${breakInfo.end}`, shopTimezone);
            const breakDuration = differenceInMinutes(breakEnd, breakStart);
            
            // NEW PHASE 2: Calculate grace cap (½ of break duration)
            const graceCap = Math.floor(breakDuration * BREAK_GRACE_CAP_FRACTION);
            
            // Check if we're approaching or in the break
            const minutesUntilBreak = differenceInMinutes(breakStart, now);
            const minutesIntoBreak = differenceInMinutes(now, breakStart);
            const isInBreak = isWithinInterval(now, { start: breakStart, end: breakEnd });
            
            if (!isInBreak && minutesUntilBreak > 5) continue; // Not approaching break yet

            // NEW PHASE 2: Incremental grace period logic
            if (isInBreak && minutesIntoBreak <= graceCap) {
              // Within grace period - slide break start minute-by-minute
              const graceKey = `break-grace-${ticket.id}-${breakInfo.start}`;
              const lastGraceMin = parseInt(localStorage.getItem(graceKey) || '0');
              
              // Only update if we've advanced by another minute
              if (minutesIntoBreak > lastGraceMin) {
                // Log grace adjustment
                console.log(
                  `[Break Grace] Ticket ${ticket.id}: Break start sliding by ${minutesIntoBreak}/${graceCap} min (${breakInfo.name})`
                );
                
                localStorage.setItem(graceKey, minutesIntoBreak.toString());
                
                // Notify user of grace period usage (every 5 minutes)
                if (minutesIntoBreak % 5 === 0) {
                  toast({
                    title: '⏰ Break Grace Period Active',
                    description: `${tech.name}'s ${breakInfo.name} delayed ${minutesIntoBreak} of ${graceCap} min`,
                    variant: 'default',
                  });
                }
              }
              continue; // Don't pause yet - still in grace
            }

            // Check if we've exceeded grace cap - show warning instead of auto-pausing
            const graceWarningKey = `break-grace-warning-${ticket.id}-${breakInfo.start}`;
            const hasGraceWarned = localStorage.getItem(graceWarningKey);
            
            // Warning when grace cap exceeded - no auto-pause
            if (isInBreak && minutesIntoBreak > graceCap && !hasGraceWarned && ticket.status === 'Active') {
              toast({
                title: '⚠️ Timer Running During Break',
                description: `${tech.name}'s ${breakInfo.name} started ${minutesIntoBreak} min ago. Timer is still running.`,
                variant: 'destructive',
              });
              
              addNotification({
                type: 'timer',
                title: 'Timer Running During Break',
                message: `${ticket.customer_name || 'Ticket'} timer running during ${tech.name}'s ${breakInfo.name}`,
                data: { 
                  ticketId: ticket.id, 
                  techId: tech.id,
                },
                targetRoles: ['MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
                targetTechId: tech.id
              });

              localStorage.setItem(graceWarningKey, 'true');
              
              // Clear grace tracking
              localStorage.removeItem(`break-grace-${ticket.id}-${breakInfo.start}`);
            }
          }
        }
      } catch (error) {
        // FIX-R132-MED-10: timer-in-break monitor runs every 30s. Persistent
        // failure means break-pause notifications stop firing — operators
        // see techs blowing past breaks but no UI alert. Push to client
        // error buffer so the failure is visible in next bug report.
        console.error('Error checking timers in breaks:', error);
        try {
          const { pushClientError } = await import('@/utils/clientErrorBuffer');
          pushClientError({
            source: 'mutation',
            message: `[useTimerBreakMonitor] check threw: ${(error as { message?: string } | undefined)?.message ?? String(error)}`,
            stack: (error as { stack?: string } | undefined)?.stack,
            extra: { hook: 'useTimerBreakMonitor' },
            shopId: shopId ?? null,
          });
        } catch {
          // never throw from a logger
        }
      }
    };

    // Check every 30 seconds
    const intervalId = setInterval(checkTimersInBreaks, 30000);
    
    // Check immediately on mount
    checkTimersInBreaks();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  // R5-H-2: shopId in deps so effect reruns when auth resolves
  }, [selectedDayLabel, shopTimezone, shopId, addNotification, toast]);

  // Clean up old localStorage entries daily
  useEffect(() => {
    const cleanupOldEntries = () => {
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith('break-warning-') || 
            key.startsWith('break-pause-') ||
            key.startsWith('break-grace-')) {
          localStorage.removeItem(key);
        }
      });
    };

    cleanupOldEntries();
  }, [selectedDayLabel]);
};
